import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import type { Translatable } from "app/pages/sales-intelligence/types";

import BaseActivatedPanel from "../../../table-actions-panel/components/BaseActivatedPanel/BaseActivatedPanel";
import {
    StyledInnerContainer,
    StyledPanelIcon,
} from "../../../table-actions-panel/components/BaseActivatedPanel/styles";
import type { BaseActivatedPanelProps } from "../../../table-actions-panel/components/BaseActivatedPanel/types";
import SelectionDDItem from "../../../table-actions-panel/components/SelectionDDItem/SelectionDDItem";
import SelectionDropdownFooter from "../../../table-actions-panel/components/SelectionDropdown/SelectionDropdownFooter/SelectionDropdownFooter";
import useActionsPanelState from "../../../table-actions-panel/hooks/useActionsPanelState";
import type SelectionDropdown from "../../../table-actions-panel/models/selection-dropdown/SelectionDropdown";
import ManualSelectionItem from "../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import type { SelectionDropdownItem } from "../../../table-actions-panel/models/selection-dropdown-item/types";

import { StyledInfoMessage, StyledSelectionDropdown, StyledSubmitButton, StyledTitle } from "./styles";

type LegacyWebsitesCRMExportPanelViewProps = BaseActivatedPanelProps & {
    label: Readonly<Translatable>;
    isDisabled: boolean;
    isLoading: boolean;
    numberOfSelectedItems: number;
    numberOfRemainingExports: number;
    numberOfResults: number;
    selectionDropdown: SelectionDropdown;
    onSelectionChange(item: Readonly<SelectionDropdownItem>): void;
    onSubmit(activeSelectionItem: Readonly<SelectionDropdownItem>): void;
};

const LegacyWebsitesCRMExportPanelView = (props: LegacyWebsitesCRMExportPanelViewProps) => {
    const {
        isDisabled,
        isLoading,
        label,
        numberOfSelectedItems,
        numberOfRemainingExports,
        numberOfResults,
        selectionDropdown,
        onSelectionChange,
        onSubmit,
    } = props;
    const state = useActionsPanelState(selectionDropdown, {
        numberOfResults,
        numberOfSelectedItems,
        onSelectionChange,
        limit: numberOfRemainingExports,
   });
    const isSubmitDisabled = isDisabled || state.isSelectionEmpty;

    useDidUpdate(() => {
        if (!isLoading) {
            onSelectionChange(state.activeSelectionItem);
        }
    }, [isLoading]);

    function handleSubmit() {
        onSubmit(state.activeSelectionItem);
    }

    return (
        <BaseActivatedPanel onDeactivate={props.onDeactivate}>
            <StyledInnerContainer>
                <StyledPanelIcon
                    iconName="upload"
                    size="xs"
                />
                <I18n
                    data-automation="actions-panel.export-label"
                    element={StyledTitle}
                    translatable={label}
                />
                <StyledSelectionDropdown
                    buttonText={state.activeSelectionItem.getSelectedTranslation()}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    footer={
                        <SelectionDropdownFooter
                            numberOfRemainingItems={numberOfRemainingExports}
                            label={translate("si.multi_selector.excel.dropdown.bottom.label")}
                        />
                    }
                    onSelect={state.setActiveSelectionItem}
                >
                    {state.dropdownItems
                        .map((item) => (
                            <SelectionDDItem
                                key={item.id}
                                item={item}
                                isSelected={state.activeSelectionItem.id === item.id}
                            />
                        ))
                        .concat(
                            <SelectionDDItem
                                item={state.manualSelectionItem}
                                key={ManualSelectionItem.ID}
                                isSelected={state.activeSelectionItem.id === ManualSelectionItem.ID}
                            />,
                        )}
                </StyledSelectionDropdown>
                <StyledSubmitButton
                    isDisabled={isSubmitDisabled}
                    isLoading={isLoading}
                    text={translate("si.common.button.export")}
                    tooltipContent={translate("si.multi_selector.tooltip.excel.unselected_domains")}
                    onClick={handleSubmit}
                />
                <I18n
                    data-automation="info-message-text"
                    element={StyledInfoMessage}
                    translatable={{ key: "si.multi_selector.crm_export_info" }}
                />
            </StyledInnerContainer>
        </BaseActivatedPanel>
    );
};

export default LegacyWebsitesCRMExportPanelView;
